/**
 * Diamond oxygen decision tools — chooser + battery setting + pick + flight planner.
 * No-JS: compare table already shows setting-2 battery defaults in HTML.
 * Data is typical/largest-battery; not a clinical guarantee or airline policy.
 *
 * Chooser: pulse setting → use → what matters (capacity filter on max setting).
 * Flight: hours × setting → batteries needed (optional 1.5× reserve).
 * Matrix: theme fallbacks; product metafields.diamond.* override when present.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-diamond-oxygen]');
  if (!root) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Typical hours at pulse settings 1–N (largest battery). Soft placeholders until metafields fill in.
  // max = highest pulse setting the unit can deliver (capacity filter for the chooser).
  var UNITS = {
    rove4: {
      name: 'Inogen Rove 4',
      flow: 'pulse',
      // 2.9 lb with standard battery. Setting 1 extended battery verified 6h 7m (6.12 hrs).
      weight: 2.9,
      price: 2695,
      best_for: 'our lightest all-around unit for everyday portability',
      use_profile: ['errands', 'home'],
      largest: [6.12, 5.13, 4.14, 3.16],
      max: 4,
      faa: true
    },
    rove6: {
      name: 'Inogen Rove 6',
      flow: 'pulse',
      // 4.8 lb. Setting 1 verified 12h 45m (12.75 hrs); settings 2–6 soft until filled.
      weight: 4.8,
      price: 2795,
      best_for: 'our best unit for full days out and higher settings',
      use_profile: ['travel', 'trip'],
      largest: [12.75, 11.2, 8.8, 6.8, 5.05, 3.95],
      max: 6,
      faa: true
    },
    igo2: {
      name: 'Drive iGo2',
      flow: 'pulse',
      weight: 5.0,
      price: 2995,
      best_for: 'a smart auto-adjusting pulse unit for active days',
      use_profile: ['errands', 'travel', 'trip'],
      // Pulse 1–5. Setting 2 verified at 3.5 hrs (largest battery); others soft until filled.
      largest: [4.3, 3.5, 2.8, 2.3, 1.8],
      max: 5,
      faa: true
    },
    liberty2: {
      // O2 Concepts Liberty 2 — only continuous-capable shoulder-bag portable in core.
      // Pulse 1–10; continuous up to 2 LPM. Pulse setting 2 verified 4.5 hrs; others soft.
      name: 'O2 Concepts Liberty 2',
      flow: 'both',
      contMax: 2,
      weight: 6.35,
      price: 2995,
      best_for:
        'the only continuous-capable shoulder-bag portable here — up to 2 LPM continuous, pulse 1–10',
      use_profile: ['home', 'travel'],
      largest: [5.0, 4.5, 3.5, 2.8, 2.0, 1.6, 1.3, 1.05, 0.85, 0.7],
      max: 10,
      faa: true,
      // Continuous hours by LPM index [0.5, 1, 1.5, 2] — soft until manufacturer fill
      cont: { largest: [5.9, 4.1, 2.9, 2.0] }
    }
  };

  // Merge product / metafield payload from Liquid when present
  try {
    var cfg = root.getAttribute('data-diamond-oxygen');
    if (cfg) {
      var parsed = JSON.parse(cfg);
      if (parsed.units) {
        Object.keys(parsed.units).forEach(function (k) {
          if (!UNITS[k]) return;
          var u = parsed.units[k];
          if (u.href) UNITS[k].href = u.href;
          if (u.name) UNITS[k].name = u.name;
          if (u.price) UNITS[k].price = u.price;
          if (u.thumb) UNITS[k].thumb = u.thumb;
          if (u.max != null && !isNaN(Number(u.max))) UNITS[k].max = Number(u.max);
          if (u.weight != null && !isNaN(Number(u.weight))) UNITS[k].weight = Number(u.weight);
          if (typeof u.faa === 'boolean') UNITS[k].faa = u.faa;
          if (Array.isArray(u.largest) && u.largest.length) {
            UNITS[k].largest = u.largest.map(function (n) {
              var x = Number(n);
              return isNaN(x) ? null : x;
            });
          }
        });
      }
    }
  } catch (e) { /* ignore */ }

  var LPMS = [0.5, 1, 1.5, 2];
  var setting = 2;
  var lpmIdx = 3;
  var mode = 'pulse'; // continuous battery mode reserved for a future section
  var answers = { rxSetting: null, use: null, matters: null };
  var currentPick = null;

  // Preference maps — only applied among units that can hit the prescribed setting
  var USE_PICK = {
    errands: 'rove4',
    travel: 'rove6',
    trip: 'rove6',
    home: 'rove4'
  };
  var MATTERS_PICK = {
    lightest: 'rove4',
    battery: 'rove6',
    price: 'rove4'
  };
  var LABELS = {
    use: {
      errands: 'Errands',
      travel: 'Full days out',
      trip: 'A trip',
      home: 'Mostly home'
    },
    matters: {
      lightest: 'Lightest',
      battery: 'Battery',
      price: 'Price'
    }
  };
  var USE_PHRASE = {
    errands: 'errands and appointments',
    travel: 'full days out',
    trip: 'travel and trips',
    home: 'time at home and sleep'
  };

  function $(sel, el) { return (el || document).querySelector(sel); }
  function $$(sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); }

  function fmtHrs(n) {
    if (n == null || isNaN(n)) return '—';
    var r = Math.round(n * 100) / 100;
    return r + ' hrs';
  }

  function eligibleKeys(need) {
    return Object.keys(UNITS).filter(function (k) {
      return UNITS[k].max >= need;
    });
  }

  /** Prefer mapped key if still eligible; else best attribute match among eligible. */
  function resolveAmong(keys, preferred, matters, need) {
    if (preferred && keys.indexOf(preferred) !== -1) return preferred;
    return bestByMatters(keys, matters, need) || keys[0] || null;
  }

  function bestByMatters(keys, matters, need) {
    if (!keys.length) return null;
    var setIdx = Math.max(0, need - 1);
    return keys.reduce(function (best, k) {
      if (!best) return k;
      var a = UNITS[k];
      var b = UNITS[best];
      if (matters === 'lightest') return a.weight < b.weight ? k : best;
      if (matters === 'price') return a.price < b.price ? k : best;
      if (matters === 'battery') {
        var ah = a.largest[setIdx];
        var bh = b.largest[setIdx];
        if (ah == null && bh == null) return a.weight <= b.weight ? k : best;
        if (ah == null) return best;
        if (bh == null) return k;
        return ah > bh ? k : best;
      }
      return best;
    }, null);
  }

  function syncSettingRadios(val) {
    $$('input[name="diamond-o2set"]').forEach(function (r) {
      r.checked = parseInt(r.value, 10) === val;
    });
    $$('input[name="diamond-q-setting"]').forEach(function (r) {
      r.checked = parseInt(r.value, 10) === val;
    });
  }

  function renderBattery() {
    var cont = mode === 'continuous';
    $$('[data-batt-unit]').forEach(function (el) {
      var key = el.getAttribute('data-batt-unit');
      var u = UNITS[key];
      if (!u) return;
      if (cont) {
        el.textContent = u.cont ? fmtHrs(u.cont.largest[lpmIdx]) : 'Pulse only';
      } else {
        var v = u.largest[setting - 1];
        el.textContent = v == null ? 'Top setting ' + u.max : fmtHrs(v);
      }
    });

    $$('[data-batt-dt]').forEach(function (el) {
      el.textContent = cont
        ? 'Battery at ' + LPMS[lpmIdx] + ' LPM continuous'
        : 'Battery';
    });

    var label = $('[data-setting-label]');
    if (label) {
      label.textContent = 'Your flow rate';
    }

    var helpPulse = $('[data-setting-help-pulse]');
    var helpCont = $('[data-setting-help-cont]');
    if (helpPulse) helpPulse.hidden = cont;
    if (helpCont) helpCont.hidden = !cont;

    var segPulse = $('[data-seg-pulse]');
    var segCont = $('[data-seg-cont]');
    if (segPulse) {
      segPulse.hidden = cont;
      segPulse.setAttribute('aria-hidden', cont ? 'true' : 'false');
    }
    if (segCont) {
      segCont.hidden = !cont;
      segCont.setAttribute('aria-hidden', cont ? 'false' : 'true');
    }

    // longest-battery mark among units that can run this setting
    var mark = $('[data-best-batt-mark]');
    if (mark) {
      mark.hidden = cont;
      if (!cont) {
        var bestKey = null;
        var bestVal = -1;
        Object.keys(UNITS).forEach(function (k) {
          if (UNITS[k].max < setting) return;
          var v = UNITS[k].largest[setting - 1];
          if (v != null && v > bestVal) {
            bestVal = v;
            bestKey = k;
          }
        });
        var host = bestKey ? $('[data-batt-unit="' + bestKey + '"]') : null;
        if (host && host.parentNode) {
          if (mark.parentNode !== host.parentNode) host.parentNode.appendChild(mark);
        }
      }
    }

    if (currentPick && !$('[data-result-card]').hidden) {
      var why = $('[data-result-why]');
      if (why) why.textContent = rationale(currentPick);
    }
  }

  function rationale(pick) {
    var u = UNITS[pick];
    if (!u) return '';
    var need = answers.rxSetting || setting;
    var usePhrase = answers.use ? USE_PHRASE[answers.use] : null;
    var fits = usePhrase
      ? (u.use_profile.indexOf(answers.use) !== -1 ? ', built for ' : ', ready for ') + usePhrase
      : '';

    var capacityNote = '';
    if (need > 4 && pick !== 'rove4') {
      capacityNote = ' Covers your prescribed setting ' + need + ' (Rove 4 tops out at 4).';
    } else if (need <= 4 && pick === 'rove4') {
      capacityNote = ' Handles settings 1–4 — enough for your prescribed setting ' + need + '.';
    } else if (need > 6) {
      capacityNote = ' Reaches pulse setting ' + need + ' when lighter units stop lower.';
    }

    var elig = eligibleKeys(need);
    var hrs = u.largest[need - 1];
    var sup = {
      lightest: elig.every(function (k) { return UNITS[k].weight >= u.weight; }),
      battery: hrs != null && elig.every(function (k) {
        var v = UNITS[k].largest[need - 1];
        return v == null || v <= hrs;
      }),
      price: elig.every(function (k) { return UNITS[k].price >= u.price; })
    };

    if (!answers.matters) {
      return (u.best_for.charAt(0).toUpperCase() + u.best_for.slice(1)) + fits + '.' + capacityNote;
    }

    var lead = {
      lightest: sup.lightest
        ? 'The lightest unit that reaches setting ' + need + ' (' + u.weight + ' lbs)'
        : 'A light option that reaches setting ' + need + ' (' + u.weight + ' lbs)',
      battery: sup.battery
        ? 'The longest battery at setting ' + need + ' among units that go that high (' + fmtHrs(hrs) + ')'
        : fmtHrs(hrs) + ' of battery at setting ' + need,
      price: sup.price
        ? 'The lowest starting price among units that reach setting ' + need + ' ($' + u.price.toLocaleString('en-US') + ')'
        : 'A strong value that reaches setting ' + need + ' ($' + u.price.toLocaleString('en-US') + ')'
    }[answers.matters];

    var second;
    if (answers.matters !== 'lightest' && sup.lightest) second = 'still the lightest eligible unit here';
    else if (answers.matters !== 'battery' && sup.battery) second = 'still the longest eligible battery here';
    else if (answers.matters !== 'price' && sup.price) second = 'still the lowest eligible price here';
    else second = u.best_for;

    if (answers.use === 'trip') {
      second = 'a solid pick when you’re planning travel days away from a plug';
    }

    return lead + fits + ' — and ' + second + '.' + capacityNote;
  }

  function clearPick() {
    $$('[data-pick-key]').forEach(function (el) {
      el.classList.remove('is-pick');
    });
    $$('[data-pick-chip]').forEach(function (el) {
      el.hidden = true;
      el.setAttribute('hidden', '');
    });
  }

  function applyPick(pick) {
    $$('[data-pick-key="' + pick + '"]').forEach(function (el) {
      el.classList.add('is-pick');
      var chip = el.querySelector('[data-pick-chip]');
      if (chip) {
        chip.hidden = false;
        chip.removeAttribute('hidden');
      }
    });
  }

  var scrollSeq = 0;
  function animateScrollTo(el, done) {
    var seq = ++scrollSeq;
    var startY = window.pageYOffset;
    var targetY = Math.max(
      0,
      startY + el.getBoundingClientRect().top - Math.round(window.innerHeight * 0.28)
    );
    if (reduced) {
      window.scrollTo(0, targetY);
      if (done) done();
      return;
    }
    var dur = 480;
    var t0 = null;
    function ease(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }
    function step(ts) {
      if (seq !== scrollSeq) return;
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      window.scrollTo(0, startY + (targetY - startY) * ease(p));
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    if (done) setTimeout(function () { if (seq === scrollSeq) done(); }, dur + 60);
  }

  function recommend() {
    var card = $('[data-result-card]');
    if (!card) return;

    // Need setting + use + matters
    if (answers.rxSetting == null || !answers.use || !answers.matters) {
      scrollSeq++;
      clearPick();
      card.hidden = true;
      currentPick = null;
      return;
    }

    var need = answers.rxSetting;
    var elig = eligibleKeys(need);
    if (!elig.length) {
      scrollSeq++;
      clearPick();
      card.hidden = true;
      currentPick = null;
      return;
    }

    // Score only among units that can deliver the prescribed setting
    var score = {};
    elig.forEach(function (k) { score[k] = 0; });

    var usePref = USE_PICK[answers.use];
    if (usePref && score[usePref] != null) score[usePref] += 2;

    var mattersPref = MATTERS_PICK[answers.matters];
    if (mattersPref && score[mattersPref] != null) {
      score[mattersPref] += 2;
    } else {
      // Mapped unit ineligible (e.g. lightest/price → Rove 4 but need setting 5+)
      var attrBest = bestByMatters(elig, answers.matters, need);
      if (attrBest) score[attrBest] += 2;
    }

    // Default pick = matters preference if eligible, else attribute best (matters wins ties)
    var pick = resolveAmong(elig, MATTERS_PICK[answers.matters], answers.matters, need);
    elig.forEach(function (k) {
      if (score[k] > score[pick]) pick = k;
    });

    var wasHidden = card.hidden;
    clearPick();
    currentPick = pick;
    var u = UNITS[pick];
    if (!u) return;

    var inputs = $('[data-result-inputs]');
    if (inputs) {
      inputs.textContent = [
        'Setting ' + need,
        answers.use && LABELS.use[answers.use],
        answers.matters && LABELS.matters[answers.matters]
      ]
        .filter(Boolean)
        .join(' · ');
    }
    var nameEl = $('[data-result-name]');
    if (nameEl) nameEl.textContent = u.name;
    var whyEl = $('[data-result-why]');
    if (whyEl) whyEl.textContent = rationale(pick);
    var priceEl = $('[data-result-price]');
    if (priceEl) {
      if (u.price) {
        priceEl.textContent = 'from $' + Number(u.price).toLocaleString('en-US');
        priceEl.hidden = false;
      } else {
        priceEl.textContent = '';
        priceEl.hidden = true;
      }
    }
    var thumb = $('[data-result-thumb]');
    if (thumb) {
      // Prefer the compare-card product image already rendered by Liquid
      // (real product media). JSON `thumb` may still point at legacy theme assets.
      var cardImg =
        $('[data-pick-key="' + pick + '"] .diamond-cat-compare__thumb-img') ||
        $('[data-pick-key="' + pick + '"] img');
      var src = '';
      if (cardImg) {
        src = cardImg.currentSrc || cardImg.getAttribute('src') || '';
      }
      if (!src && u.thumb) src = u.thumb;
      if (src) {
        thumb.src = src;
        thumb.alt = u.name || '';
        thumb.hidden = false;
      } else {
        thumb.removeAttribute('src');
        thumb.hidden = true;
      }
    }
    var view = $('[data-result-view]');
    if (view) {
      view.setAttribute('href', u.href || '#DiamondCompare');
      view.textContent = 'View product';
    }

    card.hidden = false;
    // Scroll only on first reveal — re-answering should not yank the page
    if (wasHidden) {
      animateScrollTo(card, function () {
        applyPick(pick);
      });
    } else {
      applyPick(pick);
    }
  }

  function setRxSetting(val, fromCompareBar) {
    var n = parseInt(val, 10);
    if (isNaN(n) || n < 1) return;
    setting = n;
    answers.rxSetting = n;
    if (!fromCompareBar) syncSettingRadios(n);
    else {
      $$('input[name="diamond-q-setting"]').forEach(function (r) {
        r.checked = parseInt(r.value, 10) === n;
      });
    }
    // Keep flight planner setting in sync
    $$('input[name="diamond-flight-set"]').forEach(function (r) {
      r.checked = parseInt(r.value, 10) === n;
    });
    renderBattery();
    recommend();
    renderFlight();
  }

  // —— Flight planner (Phase 3) ——
  function batteriesNeeded(flightHrs, cellHrs, useBuffer) {
    if (cellHrs == null || cellHrs <= 0 || flightHrs <= 0) return null;
    var need = useBuffer ? flightHrs * 1.5 : flightHrs;
    return Math.max(1, Math.ceil(need / cellHrs - 1e-9));
  }

  function renderFlight() {
    var panel = $('[data-diamond-flight]');
    if (!panel) return;

    var hoursEl = $('[data-flight-hours]', panel);
    var list = $('[data-flight-list]', panel);
    var placeholder = $('[data-flight-placeholder]', panel);
    if (!hoursEl || !list) return;

    var flightHrs = parseFloat(hoursEl.value);
    var setEl = $('input[name="diamond-flight-set"]:checked', panel);
    var flightSet = setEl ? parseInt(setEl.value, 10) : setting;
    if (isNaN(flightSet) || flightSet < 1) flightSet = setting || 2;

    var bufferEl = $('[data-flight-buffer]', panel);
    var useBuffer = bufferEl ? bufferEl.checked : true;

    if (isNaN(flightHrs) || flightHrs <= 0) {
      list.hidden = true;
      list.innerHTML = '';
      if (placeholder) placeholder.hidden = false;
      return;
    }

    if (placeholder) placeholder.hidden = true;
    list.hidden = false;
    list.innerHTML = '';

    Object.keys(UNITS).forEach(function (k) {
      var u = UNITS[k];
      var li = document.createElement('li');
      li.className = 'diamond-flight__row';

      var name = document.createElement('p');
      name.className = 'diamond-flight__name';
      name.textContent = u.name;

      var meta = document.createElement('p');
      meta.className = 'diamond-flight__meta';

      var badge = document.createElement('span');
      badge.className = 'diamond-flight__badge';

      if (u.faa === false) {
        li.classList.add('is-out');
        meta.textContent = 'Not marked FAA-approved in our data — call us before flying.';
        badge.textContent = 'Call us';
      } else if (u.max < flightSet) {
        li.classList.add('is-out');
        meta.textContent = 'Tops out at setting ' + u.max + ' — doesn’t reach setting ' + flightSet + '.';
        badge.textContent = 'Setting too low';
      } else {
        var cell = u.largest[flightSet - 1];
        if (cell == null) {
          li.classList.add('is-out');
          meta.textContent = 'No battery data at setting ' + flightSet + '.';
          badge.textContent = 'Call us';
        } else {
          var packs = batteriesNeeded(flightHrs, cell, useBuffer);
          var reserveNote = useBuffer ? ' with 1.5× reserve' : '';
          meta.textContent =
            fmtHrs(cell) +
            ' per battery at setting ' +
            flightSet +
            ' · ' +
            packs +
            (packs === 1 ? ' battery' : ' batteries') +
            ' for ' +
            flightHrs +
            ' hr flight' +
            reserveNote;
          if (packs <= 1) {
            li.classList.add('is-ok');
            badge.textContent = '1 battery';
          } else if (packs === 2) {
            li.classList.add('is-ok');
            badge.textContent = packs + ' batteries';
          } else {
            li.classList.add('is-tight');
            badge.textContent = packs + ' batteries';
          }
        }
      }

      li.appendChild(name);
      li.appendChild(meta);
      li.appendChild(badge);
      list.appendChild(li);
    });
  }

  function bindFlight() {
    var panel = $('[data-diamond-flight]');
    if (!panel) return;

    var hoursEl = $('[data-flight-hours]', panel);
    if (hoursEl) {
      hoursEl.addEventListener('input', renderFlight);
      hoursEl.addEventListener('change', renderFlight);
    }
    $$('input[name="diamond-flight-set"]', panel).forEach(function (r) {
      r.addEventListener('change', function () {
        // Optionally push flight setting into main setting bar for battery table
        setRxSetting(r.value, false);
      });
    });
    var bufferEl = $('[data-flight-buffer]', panel);
    if (bufferEl) bufferEl.addEventListener('change', renderFlight);

    // Seed flight setting from current page setting
    $$('input[name="diamond-flight-set"]', panel).forEach(function (r) {
      r.checked = parseInt(r.value, 10) === setting;
    });
    renderFlight();
  }

  // Compare-bar setting radios
  $$('input[name="diamond-o2set"]').forEach(function (r) {
    r.addEventListener('change', function () {
      setRxSetting(r.value, true);
    });
  });
  $$('input[name="diamond-o2lpm"]').forEach(function (r) {
    r.addEventListener('change', function () {
      lpmIdx = LPMS.indexOf(parseFloat(r.value));
      if (lpmIdx < 0) lpmIdx = 3;
      renderBattery();
    });
  });

  // Chooser: setting first, then use, then matters
  $$('input[name="diamond-q-setting"]').forEach(function (r) {
    r.addEventListener('change', function () {
      setRxSetting(r.value, false);
    });
  });

  function bindChooser(name, key) {
    $$('input[name="' + name + '"]').forEach(function (r) {
      r.addEventListener('change', function () {
        answers[key] = r.value;
        recommend();
      });
    });
  }
  bindChooser('diamond-q-use', 'use');
  bindChooser('diamond-q-matters', 'matters');

  // Init from compare bar if checked; chooser setting still required for a pick
  var checked = $('input[name="diamond-o2set"]:checked');
  if (checked) {
    setting = parseInt(checked.value, 10) || 2;
  }
  renderBattery();
  bindFlight();
})();
